import { useMemo } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { motion, useReducedMotion } from "framer-motion";
import { Bath, Hammer, Home, TreePine, Clock, ArrowRight, type LucideIcon } from "lucide-react";
import { CloudinaryImage } from "@/components/CloudinaryMedia";
import { usePublicMedia, findMediaByTag, type PublicMedia } from "@/hooks/useMedia";

interface ServiceItem {
  key: string; // Used to match with CMS tag
  title: string;
  description: string;
  timeline: string;
  warranty: string;
  icon: LucideIcon;
  emergency?: boolean;
}

// Service definitions (static content)
const serviceDefinitions: ServiceItem[] = [
  {
    key: "bathroom",
    title: "Bathroom Renovations",
    description: "From outdated tubs to modern, spa-like bathrooms — we handle everything from demolition to final fixtures. Walk-in showers, freestanding tubs, custom vanities. New plumbing, tiling, waterproofing, and electrical.",
    timeline: "3–5 weeks",
    warranty: "1-year warranty on labor",
    icon: Bath,
  },
  {
    key: "kitchen",
    title: "Kitchen Remodeling",
    description: "Custom kitchens designed for how you cook, live, and entertain — built to last in Houston's climate. Layout changes, open-concept conversions, and island installs. Custom or semi-custom cabinets, countertops, and backsplashes.",
    timeline: "5–8 weeks",
    warranty: "1-year warranty on labor",
    icon: Home,
  },
  {
    key: "renovation",
    title: "Whole-Home Renovations",
    description: "From single-room makeovers to full-home transformations — we manage the project from concept to completion. Living rooms, bedrooms, home offices, and additions. Dedicated project manager as your single point of contact.",
    timeline: "Varies by scope",
    warranty: "1-year warranty on labor",
    icon: Hammer,
  },
  {
    key: "woodwork",
    title: "Carpentry & Custom Woodwork",
    description: "Custom carpentry that adds character, storage, and value to your home. Built-in shelving, media walls, and storage solutions. Custom decks, pergolas, and outdoor structures. Designed to withstand Houston's humidity and heat.",
    timeline: "2–4 weeks",
    warranty: "1-year warranty on labor",
    icon: TreePine,
  },
  {
    key: "handyman",
    title: "24/7 Handyman & Emergency Repair",
    description: "Fast, professional repairs and small projects done right the first time — whenever you need us. Minor drywall repairs, leaks, and fixture replacements. Door, window, and hardware repairs. 24/7 emergency service available.",
    timeline: "Same-day available",
    warranty: "Warranty on all work performed",
    icon: Clock,
    emergency: true,
  },
];

/**
 * Get a seeded random number for consistent random selection
 * Uses the service key to ensure same service always gets same random image
 */
function seededRandom(seed: string): number {
  let hash = 0;
  for (let i = 0; i < seed.length; i++) {
    const char = seed.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  return Math.abs(hash);
}

/**
 * Get image for a service:
 * 1. Uses CMS image with matching service tag if available
 * 2. Falls back to random image from gallery/portfolio
 * 3. Returns null if no images available
 */
function getServiceImage(
  service: ServiceItem, 
  serviceMedia: PublicMedia[] | undefined,
  fallbackMedia: PublicMedia[] | undefined
): {
  publicId: string;
  isCloudinary: true;
} | null {
  // First, try to find media with matching service tag
  if (serviceMedia && serviceMedia.length > 0) {
    const match = findMediaByTag(serviceMedia, service.key);
    if (match) {
      return {
        publicId: match.publicId,
        isCloudinary: true,
      };
    }
  }
  
  // Fallback: randomly pick from gallery/portfolio images
  if (fallbackMedia && fallbackMedia.length > 0) {
    // Use seeded random so the same service always gets the same fallback image
    const randomIndex = seededRandom(service.key) % fallbackMedia.length;
    const fallback = fallbackMedia[randomIndex];
    return {
      publicId: fallback.publicId,
      isCloudinary: true,
    };
  }
  
  // No image available
  return null;
}

export default function Services() {
  const prefersReducedMotion = useReducedMotion();
  
  // Fetch service images from CMS
  const { data: serviceMedia } = usePublicMedia("service");
  
  // Fetch gallery and portfolio images as fallback
  const { data: galleryMedia } = usePublicMedia("gallery", { resourceType: "image" });
  const { data: portfolioMedia } = usePublicMedia("portfolio", { resourceType: "image" });
  
  // Combine gallery and portfolio for fallback pool
  const fallbackMedia = useMemo(() => {
    const combined: PublicMedia[] = [];
    if (galleryMedia) combined.push(...galleryMedia);
    if (portfolioMedia) combined.push(...portfolioMedia);
    return combined.length > 0 ? combined : undefined;
  }, [galleryMedia, portfolioMedia]);

  // Build services with dynamic images
  const services = useMemo(() => {
    return serviceDefinitions.map((service) => ({
      ...service,
      image: getServiceImage(service, serviceMedia, fallbackMedia),
    }));
  }, [serviceMedia, fallbackMedia]);
  
  const containerVariants = {
    hidden: { opacity: prefersReducedMotion ? 1 : 0 },
    visible: {
      opacity: 1,
      transition: prefersReducedMotion ? { duration: 0 } : {
        staggerChildren: 0.15,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: prefersReducedMotion ? 1 : 0, y: prefersReducedMotion ? 0 : 40 },
    visible: {
      opacity: 1,
      y: 0,
      transition: prefersReducedMotion ? { duration: 0 } : {
        duration: 0.8,
        ease: [0.22, 1, 0.36, 1],
      },
    },
  };

  return (
    <section id="services" className="py-16 md:py-24 lg:py-32 bg-background relative overflow-hidden" data-testid="section-services">
      {/* Subtle gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#00d9ff]/5 via-transparent to-transparent pointer-events-none" />
      
      <div className="max-w-7xl mx-auto px-4 md:px-8 relative z-10">
        <motion.div
          initial={{ opacity: prefersReducedMotion ? 1 : 0, y: prefersReducedMotion ? 0 : -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={prefersReducedMotion ? { duration: 0 } : { duration: 0.6 }}
          className="text-center mb-12 md:mb-16"
        >
          <h2
            className="text-4xl md:text-5xl lg:text-6xl font-heading font-bold gradient-text mb-4 tracking-tight"
            data-testid="text-services-title"
          >
            Our Services
          </h2>
          <p
            className="text-lg md:text-xl text-[#e0e0e0]/70 max-w-2xl mx-auto"
            data-testid="text-services-subtitle"
          >
            Professional construction and renovation services crafted with precision and innovation
          </p>
        </motion.div>

        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-50px" }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-10"
        >
          {services.map((service, index) => {
            const Icon = service.icon;
            const hasImage = service.image !== null;
            
            return (
              <motion.div key={index} variants={itemVariants}>
                <Card
                  className="group glass-card hover-lift hover-glow-cyan transition-all duration-500 h-full flex flex-col overflow-hidden"
                  data-testid={`card-service-${index}`}
                >
                  {/* Image Section - only shown when CMS image is available */}
                  {hasImage && (
                    <div className="relative h-64 overflow-hidden">
                      <CloudinaryImage
                        publicId={service.image.publicId}
                        alt={service.title}
                        width={600}
                        height={400}
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-[#0a0e27]/95 via-[#0a0e27]/60 to-transparent" />
                      
                      {/* Icon Overlay */}
                      <div className="absolute top-4 right-4 bg-[#00d9ff]/20 backdrop-blur-md p-3 rounded-lg border border-[#00d9ff]/30 group-hover:bg-[#00d9ff]/30 transition-all duration-300">
                        <Icon className="h-7 w-7 text-[#00d9ff]" />
                      </div>
                    </div>
                  )}
                  
                  {/* Icon header when no image - provides visual interest */}
                  {!hasImage && (
                    <div className="relative h-24 overflow-hidden bg-gradient-to-br from-[#00d9ff]/10 via-[#0a0e27] to-[#ffd700]/5">
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="bg-[#00d9ff]/20 backdrop-blur-md p-4 rounded-xl border border-[#00d9ff]/30 group-hover:bg-[#00d9ff]/30 transition-all duration-300">
                          <Icon className="h-10 w-10 text-[#00d9ff]" />
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Content Section */}
                  <div className="p-6 flex-1 flex flex-col">
                    <h3
                      className="text-2xl font-heading font-semibold text-[#00d9ff] mb-3 group-hover:text-[#ffd700] transition-colors duration-300"
                      data-testid={`text-service-title-${index}`}
                    >
                      {service.title}
                    </h3>
                    <p
                      className="text-[#e0e0e0]/70 leading-relaxed mb-4 flex-1"
                      data-testid={`text-service-description-${index}`}
                    >
                      {service.description}
                    </p>

                    {/* Timeline and Warranty Info */}
                    <div className="space-y-2 mb-6">
                      {service.timeline && (
                        <p className="text-sm text-gray-400">
                          <span className="text-[#00d9ff] font-semibold">Timeline:</span> {service.timeline}
                        </p>
                      )}
                      {service.warranty && (
                        <p className="text-sm text-gray-400">
                          <span className="text-[#ffd700] font-semibold">{service.warranty}</span>
                        </p>
                      )}
                      {service.emergency && (
                        <p className="text-sm text-[#ffd700] font-semibold">
                          ⚡ 24/7 Emergency Service Available
                        </p>
                      )}
                    </div>

                    <Button
                      variant="ghost"
                      className="w-full justify-between group/btn text-[#00d9ff] hover:text-[#ffd700] hover:bg-[#00d9ff]/10 transition-all duration-300"
                      onClick={() => {
                        const element = document.getElementById("contact");
                        if (element) {
                          element.scrollIntoView({ behavior: "smooth" });
                        }
                      }}
                      data-testid={`button-service-learn-more-${index}`}
                    >
                      <span>Learn More</span>
                      <ArrowRight className="h-5 w-5 transition-transform group-hover/btn:translate-x-1" />
                    </Button>
                  </div>
                </Card>
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
}
